import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { KeycloakService } from 'keycloak-angular';
import {
  AclApdApiService,
  AuthApiService,
  CatalogueApiService,
  FileApiService,
  RsApiService,
} from '@angular-workspace/services';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { ModelPopupComponent } from '../../../shared/components/popups/model-popup/model-popup.component';
import { GatewayDataComponent } from '../../../shared/components/popups/gateway-data/gateway-data.component';
import { OgcStacDataComponent } from '../../../shared/components/popups/ogc-stac-data/ogc-stac-data.component';
import { OgcFeatureDataComponent } from '../../../shared/components/popups/ogc-feature-data/ogc-feature-data.component';
import { ToasterService } from '../../../services/toaster.service';
import { DateRangePickerComponent, type DateRange } from '../../../shared/components/primeng/date-range-picker/date-range-picker.component';
import { environment } from '../../../../environments/environment';

export interface DatasetResourceServer {
  readonly name?: string;
  readonly url?: string;
  readonly queryTypes?: readonly string[];
  readonly accessTypes?: readonly string[];
  readonly datasetType?: string;
  readonly resourceType?: string;
}

type FeatureDataMode = {
  type: 'GATEWAY' | 'OGC_STAC' | 'OGC_FEATURE' | 'DEFAULT';
  dataModel?: 'model1' | 'model2';
  accessTypes?: readonly string[];
};

@Component({
  selector: 'app-dataset-detail-dataset-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalComponent,
    LoginModalComponent,
    ModelPopupComponent,
    GatewayDataComponent,
    OgcStacDataComponent,
    OgcFeatureDataComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './dataset-tab.component.html',
  styleUrl: './dataset-tab.component.scss',
})
export class DatasetDetailDatasetTabComponent {
  private readonly rsApi = inject(RsApiService);
  private readonly fileApi = inject(FileApiService);
  private readonly aclApdApi = inject(AclApdApiService);
  private readonly authApi = inject(AuthApiService);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly toaster = inject(ToasterService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);

  readonly datasetId = input.required<string>();
  readonly detailRows = input.required<Record<string, string>>();
  readonly datasetDescription = input<string>('');
  readonly datasetTags = input<string[]>([]);
  readonly activityMetrics = input({
    likes: 0,
    views: 0,
    downloads: 0,
  });
  readonly sampleData = input<unknown>(null);
  /** Synced from parent after Keycloak check / login modal dismiss. */
  readonly isLoggedIn = input<boolean>(false);
  readonly viewCodeText = input<string>('');
  readonly getDataSnippet = input<string>('');
  readonly resourceServersList = input<readonly DatasetResourceServer[]>([]);
  readonly fullDatasetPayload = input<Record<string, unknown> | null>(null);

  readonly authRefreshRequested = output<void>();

  /** ACL-APD `has_access` result shape; `type` is e.g. `urn:dx:apdServerPanel:success`. */
  readonly accessStatus = signal<{ type?: string }>({});

  /** Query / access type badges for Resource Server Details table. */
  resourceServerQueryTypeLabels(rs: DatasetResourceServer): readonly string[] {
    const qt = rs?.queryTypes;
    if (Array.isArray(qt) && qt.length > 0) {
      return qt.map((t) => String(t).toUpperCase());
    }
    const at = rs?.accessTypes;
    if (Array.isArray(at) && at.length > 0) {
      return at.map((t) => String(t).toUpperCase());
    }
    return [];
  }

  resourceServerDisplayName(rs: DatasetResourceServer): string {
    const raw =
      rs?.name ??
      rs?.resourceType ??
      rs?.datasetType ??
      '';
    const s = String(raw).trim();
    return s ? s.toUpperCase() : '—';
  }

  constructor() {
    effect(() => {
      this.datasetId();
      this.detailRows();
      void this.checkUserAccess();
    });
  }

  showSampleDataModal = false;
  showViewCodeModal = false;
  showGetDataModal = false;
  showLoginModal = false;
  showRequestAccessModal = false;
  showQueryDataModal = false;

  queryDataDateRange: DateRange = { startDate: null, endDate: null };
  queryDataLoading = false;
  queryDataResponse: unknown = null;

  isFeatureDataOpen = false;
  featureDataMode: FeatureDataMode = {
    type: 'GATEWAY',
    dataModel: 'model1',
    accessTypes: [],
  };

  getDataLoading = false;
  getDataPdfUrl: string | null = null;
  getDataFileDownloadUrl: string | null = null;
  getDataFileName = '';

  readonly PURPOSE_MAX_LENGTH = 200;
  requestForm: {
    requestAs: 'Individual' | 'Organisation';
    organisationName: string;
    purposeDescription: string;
    agreeToTerms: boolean;
  } = {
    requestAs: 'Organisation',
    organisationName: '',
    purposeDescription: '',
    agreeToTerms: false,
  };
  isOrganisationNameDisabled = false;
  userHasOrganisation = false;
  isSubmittingRequest = false;

  readonly sampleDataJson = computed(() => {
    const s = this.sampleData();
    if (s == null) return '';
    try {
      return typeof s === 'string' ? s : JSON.stringify(s, null, 2);
    } catch {
      return String(s);
    }
  });

  /** Catalogue `dataSample` / `data_sample` present and non-empty (MahaAgX `hasDataSample`). */
  readonly hasDataSample = computed(() => {
    const d = this.sampleData();
    if (d == null) return false;
    if (typeof d === 'string') return d.trim().length > 0;
    if (Array.isArray(d)) return d.length > 0;
    if (typeof d === 'object') return Object.keys(d as object).length > 0;
    return true;
  });

  readonly isRestrictedDataset = computed(
    () => (this.detailRows()['datasetPermission'] ?? '').toLowerCase() === 'restricted',
  );

  /**
   * MahaAgX: Get Data disabled when not logged in, or restricted dataset without
   * `has_access` success (mirrors `shouldDisableRsActionsUntilAccessOk` for this action).
   */
  readonly getDataDisabled = computed(() => {
    if (!this.isLoggedIn()) return true;
    if (!this.isRestrictedDataset()) return false;
    return this.accessStatus().type !== 'urn:dx:apdServerPanel:success';
  });

  readonly getDataDisabledTitle = computed(() => {
    if (!this.isLoggedIn()) return 'Sign in to get data';
    if (
      this.isRestrictedDataset() &&
      this.accessStatus().type !== 'urn:dx:apdServerPanel:success'
    ) {
      return 'Request access to fetch this dataset';
    }
    return '';
  });

  readonly showDownloadButton = computed(() => {
    if (!this.isRestrictedDataset()) return true;
    return this.accessStatus().type === 'urn:dx:apdServerPanel:success';
  });

  readonly showRequestAccessButton = computed(() => {
    if (!this.isRestrictedDataset()) return false;
    return this.accessStatus().type !== 'urn:dx:apdServerPanel:success';
  });

  async checkUserAccess(): Promise<void> {
    const id = this.datasetId();
    if (!id) return;
    if (!this.isRestrictedDataset()) {
      this.accessStatus.set({ type: 'urn:dx:apdServerPanel:success' });
      return;
    }
    let loggedIn = false;
    try {
      loggedIn = await this.keycloak.isLoggedIn();
    } catch {
      loggedIn = false;
    }
    if (!loggedIn) {
      this.accessStatus.set({});
      return;
    }
    try {
      const res = await this.aclApdApi.hasAccess({ itemId: id });
      if (res && typeof res === 'object') {
        this.accessStatus.set(res as { type?: string });
      }
    } catch (err: unknown) {
      const e = err as { error?: { type?: string } };
      if (e?.error && typeof e.error === 'object') {
        this.accessStatus.set(e.error as { type?: string });
      } else {
        this.accessStatus.set({});
      }
    }
  }

  get purposeDescriptionLength(): number {
    return this.requestForm.purposeDescription.length;
  }

  get isPurposeDescriptionValid(): boolean {
    return this.purposeDescriptionLength <= this.PURPOSE_MAX_LENGTH;
  }

  get isRequestFormValid(): boolean {
    const orgOk =
      this.requestForm.requestAs === 'Individual' ||
      (this.requestForm.requestAs === 'Organisation' &&
        this.requestForm.organisationName.trim().length > 0);
    return (
      orgOk &&
      this.requestForm.purposeDescription.trim().length > 0 &&
      this.isPurposeDescriptionValid &&
      this.requestForm.agreeToTerms
    );
  }

  get getDataPdfSafeUrl(): SafeResourceUrl | null {
    const u = this.getDataPdfUrl;
    return u ? this.sanitizer.bypassSecurityTrustResourceUrl(u) : null;
  }

  formatMetric(n: number | undefined): string {
    const v = n ?? 0;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(v);
  }

  parseFormats(raw: string): string[] {
    if (!raw?.trim()) return [];
    return raw
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  openSampleData(): void {
    if (!this.sampleDataJson()) {
      this.toaster.info('No sample data available for this dataset.');
      return;
    }
    this.showSampleDataModal = true;
  }

  openViewCode(): void {
    this.showViewCodeModal = true;
  }

  async copySampleData(): Promise<void> {
    const text = this.sampleDataJson();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      this.toaster.success('Sample data copied to clipboard');
    } catch {
      this.toaster.error('Could not copy to clipboard');
    }
  }

  async copyCode(): Promise<void> {
    const text = this.viewCodeText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      this.toaster.success('Code copied to clipboard');
    } catch {
      this.toaster.error('Could not copy to clipboard');
    }
  }

  downloadCode(): void {
    const text = this.viewCodeText();
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dataset-${this.datasetId()}-code.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private hasQueryTypeGlobal(type: string): boolean {
    const data = this.fullDatasetPayload();
    if (!data) return false;
    const upper = type.toUpperCase();
    const apis = data['iudxResourceAPIs'];
    if (
      Array.isArray(apis) &&
      apis.some((t: unknown) => String(t).toUpperCase() === upper)
    ) {
      return true;
    }
    const list = this.resourceServersList();
    return list.some((r) => {
      const qt = r?.queryTypes;
      return (
        Array.isArray(qt) &&
        qt.some((t: string) => String(t).toUpperCase() === upper)
      );
    });
  }

  private hasFileResource(): boolean {
    const data = this.fullDatasetPayload();
    if (!data) return false;
    const apis = data['iudxResourceAPIs'];
    if (
      Array.isArray(apis) &&
      apis.some((t: unknown) => String(t).toUpperCase() === 'FILE')
    ) {
      return true;
    }
    const list = this.resourceServersList();
    return list.some((r) => {
      const name = (r?.name ?? '').toString().toUpperCase();
      const datasetType = (r?.datasetType ?? '').toString().toUpperCase();
      const resourceType = (r?.resourceType ?? '').toString().toUpperCase();
      return (
        name === 'FILE' || datasetType === 'FILE' || resourceType === 'FILE'
      );
    });
  }

  private isFileOnlyDataset(): boolean {
    return this.hasFileResource() && !this.hasQueryTypeGlobal('ATTR');
  }

  /** Show Query Data button when the dataset supports TEMPORAL query type. */
  get canShowQueryData(): boolean {
    return this.hasQueryTypeGlobal('TEMPORAL');
  }

  get isQueryDataFormValid(): boolean {
    const from = this.queryDataDateRange?.startDate;
    const to = this.queryDataDateRange?.endDate;
    if (!from || !to) return false;
    return to >= from;
  }

  get queryDataDateError(): string | null {
    const from = this.queryDataDateRange?.startDate;
    const to = this.queryDataDateRange?.endDate;
    if (!from || !to) return null;
    if (to < from) return 'End date must be on or after start date.';
    return null;
  }

  private formatDateForApi(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  async onQueryData(): Promise<void> {
    try {
      const loggedIn = await this.keycloak.isLoggedIn();
      if (!loggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }
    this.queryDataDateRange = { startDate: null, endDate: null };
    this.queryDataResponse = null;
    this.showQueryDataModal = true;
  }

  onQueryDataModalVisibleChange(visible: boolean): void {
    this.showQueryDataModal = visible;
    if (!visible) {
      this.queryDataLoading = false;
      this.queryDataResponse = null;
    }
  }

  onQueryDataDateRangeChange(value: DateRange | null): void {
    this.queryDataDateRange = value ?? { startDate: null, endDate: null };
    this.queryDataResponse = null;
  }

  onQueryDataReset(): void {
    this.queryDataDateRange = { startDate: null, endDate: null };
    this.queryDataResponse = null;
  }

  async onQueryDataSearch(): Promise<void> {
    if (!this.isQueryDataFormValid) {
      this.toaster.error(this.queryDataDateError ?? 'Please select a valid date range.');
      return;
    }
    const id = this.datasetId();
    if (!id) {
      this.toaster.error('Dataset ID is not available.');
      return;
    }
    const from = this.formatDateForApi(this.queryDataDateRange.startDate!);
    const to = this.formatDateForApi(this.queryDataDateRange.endDate!);
    const payload = {
      searchCriteria: [
        {
          searchType: 'betweenTemporal',
          field: 'observationDateTime',
          values: [`${from}T00:00:00+0530`, `${to}T23:59:59+0530`],
        },
      ],
    };
    this.queryDataLoading = true;
    try {
      const response = await this.catalogueApi.searchEntities(
        id,
        payload,
        environment.baseRsPathUrl,
        { page: 1, size: 10 },
      );
      this.queryDataResponse = response;
    } catch (err: unknown) {
      const e = err as { name?: string; error?: { detail?: string }; message?: string };
      if (e?.name === 'AbortError') return;
      this.toaster.error(
        e?.error?.detail ?? e?.message ?? 'Failed to load temporal data. Please try again.',
      );
    } finally {
      this.queryDataLoading = false;
    }
  }

  getQueryDataResponseFormatted(): string {
    if (!this.queryDataResponse) return '';
    try {
      return JSON.stringify(this.queryDataResponse, null, 2);
    } catch {
      return String(this.queryDataResponse);
    }
  }

  downloadQueryDataJson(): void {
    if (!this.queryDataResponse) return;
    const jsonStr = JSON.stringify(this.queryDataResponse, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const from = this.queryDataDateRange?.startDate
      ? this.formatDateForApi(this.queryDataDateRange.startDate)
      : 'start';
    const to = this.queryDataDateRange?.endDate
      ? this.formatDateForApi(this.queryDataDateRange.endDate)
      : 'end';
    link.download = `query-data_${from}_to_${to}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async onGetData(): Promise<void> {
    const id = this.datasetId();
    if (!id) return;
    try {
      const loggedIn = await this.keycloak.isLoggedIn();
      if (!loggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    const list = [...this.resourceServersList()];
    const nameMatches = (s: DatasetResourceServer, t: string) =>
      String(s?.name ?? '')
        .toUpperCase()
        .includes(t);
    const hasQ = (s: DatasetResourceServer, t: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((q) =>
        String(q).toUpperCase().includes(t),
      );
    const hasOgcStac = list.some((s) => nameMatches(s, 'OGC') && hasQ(s, 'STAC'));
    const hasOgcFeature = list.some(
      (s) => nameMatches(s, 'OGC') && hasQ(s, 'FEATURE'),
    );

    if (hasOgcStac || hasOgcFeature) {
      this.openServerSpecificGetDataModal();
    } else if (this.isFileOnlyDataset()) {
      this.showGetDataModal = true;
      this.getDataPdfUrl = null;
      this.getDataFileDownloadUrl = null;
      this.getDataFileName = '';
      await this.loadGetDataFile();
    } else {
      this.openServerSpecificGetDataModal();
    }
  }

  private openServerSpecificGetDataModal(): void {
    const list = [...this.resourceServersList()];
    const nameMatches = (server: DatasetResourceServer, target: string) => {
      const name = (server?.name ?? '').toString().toUpperCase();
      return name === target || name.includes(target);
    };
    const hasQueryType = (server: DatasetResourceServer, type: string) => {
      if (!Array.isArray(server.queryTypes)) return false;
      const target = type.toUpperCase();
      return server.queryTypes.some((t) =>
        String(t).toUpperCase().includes(target),
      );
    };

    const ogcStacServer = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'STAC'),
    );
    if (ogcStacServer) {
      this.featureDataMode = {
        type: 'OGC_STAC',
        accessTypes: ogcStacServer.accessTypes ?? [],
      };
      this.isFeatureDataOpen = true;
      return;
    }

    const ogcFeatureServer = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'FEATURE'),
    );
    if (ogcFeatureServer) {
      this.featureDataMode = {
        type: 'OGC_FEATURE',
        accessTypes: ogcFeatureServer.accessTypes ?? [],
      };
      this.isFeatureDataOpen = true;
      return;
    }

    const gatewayServer = list.find((s) => nameMatches(s, 'GATEWAY'));
    if (gatewayServer) {
      const accessTypes = Array.isArray(gatewayServer.accessTypes)
        ? gatewayServer.accessTypes
        : [];
      const hasAttr = accessTypes.some((t) => String(t).toUpperCase() === 'ATTR');
      const hasTemporal = accessTypes.some(
        (t) => String(t).toUpperCase() === 'TEMPORAL',
      );
      const dataModel: 'model1' | 'model2' =
        hasAttr && hasTemporal ? 'model2' : 'model1';
      this.featureDataMode = {
        type: 'GATEWAY',
        dataModel,
        accessTypes,
      };
      this.isFeatureDataOpen = true;
      return;
    }

    this.featureDataMode = {
      type: 'DEFAULT',
      dataModel: 'model1',
      accessTypes: [],
    };
    this.isFeatureDataOpen = true;
  }

  onCloseFeatureData(): void {
    this.isFeatureDataOpen = false;
  }

  get featureDataResourceItem(): {
    id: string;
    label: string;
    name?: string;
    ogcServerBaseUrl?: string;
  } {
    const rows = this.detailRows();
    const id = this.datasetId() ?? 'dataset';
    const list = [...this.resourceServersList()];
    const nameMatches = (s: DatasetResourceServer, n: string) =>
      String(s?.name ?? '')
        .toUpperCase()
        .includes(n);
    const hasQueryType = (s: DatasetResourceServer, type: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((t) =>
        String(t).toUpperCase().includes(String(type).toUpperCase()),
      );
    const ogcFeature = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'FEATURE'),
    );
    const url = ogcFeature?.url;
    const ogcServerBaseUrl = url
      ? String(url).replace(/\/$/, '')
      : undefined;
    const labelBase =
      rows['organisationName'] ||
      rows['publisher'] ||
      'Dataset';
    return {
      id,
      label: `${labelBase} Dataset`,
      name: `${labelBase} Dataset`,
      ogcServerBaseUrl,
    };
  }

  get stacResourceItem(): {
    id: string;
    label: string;
    fileFormat: string;
    stacServerBaseUrl?: string;
  } {
    const rows = this.detailRows();
    const id = this.datasetId() ?? 'dataset';
    const list = [...this.resourceServersList()];
    const nameMatches = (s: DatasetResourceServer, n: string) =>
      String(s?.name ?? '')
        .toUpperCase()
        .includes(n);
    const hasQueryType = (s: DatasetResourceServer, type: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((t) =>
        String(t).toUpperCase().includes(String(type).toUpperCase()),
      );
    const ogcStac = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'STAC'),
    );
    const url = ogcStac?.url;
    const stacServerBaseUrl = url
      ? String(url).replace(/\/$/, '')
      : undefined;
    const labelBase =
      rows['organisationName'] ||
      rows['publisher'] ||
      'Dataset';
    return {
      id,
      label: `${labelBase} Dataset`,
      fileFormat: rows['fileFormat'] ?? '',
      stacServerBaseUrl,
    };
  }

  get gatewayResourceItem(): { id: string; name?: string; label?: string } {
    const rows = this.detailRows();
    const id = this.datasetId() ?? '';
    const labelBase =
      rows['organisationName'] ||
      rows['publisher'] ||
      'Dataset';
    return {
      id,
      name: `${labelBase} Dataset`,
      label: `${labelBase} Dataset`,
    };
  }

  private extractFilesFromFilesResponse(data: unknown): unknown[] {
    if (!data || typeof data !== 'object') return [];
    const o = data as Record<string, unknown>;
    if (Array.isArray(o['files'])) return o['files'] as unknown[];
    const inner = o['data'];
    if (
      inner &&
      typeof inner === 'object' &&
      Array.isArray((inner as Record<string, unknown>)['files'])
    ) {
      return (inner as Record<string, unknown>)['files'] as unknown[];
    }
    return [];
  }

  async loadGetDataFile(): Promise<void> {
    const id = this.datasetId();
    if (!id) return;
    this.getDataLoading = true;
    this.getDataPdfUrl = null;
    this.getDataFileDownloadUrl = null;
    this.getDataFileName = '';
    try {
      const filesResponse = await this.fileApi.getFilesForPreview(id);
      const apiFiles = this.extractFilesFromFilesResponse(filesResponse?.data);
      const files = apiFiles
        .filter((f: unknown) => {
          const row = f as Record<string, unknown>;
          return row['isFile'] !== false;
        })
        .map((f: unknown) => {
          const row = f as Record<string, unknown>;
          return {
            name: String(row['key'] ?? row['name'] ?? ''),
          };
        })
        .filter((f) => f.name);
      const firstPdf = files.find((f) => f.name.toLowerCase().endsWith('.pdf'));
      const first = firstPdf ?? files[0];
      if (!first?.name) {
        this.toaster.error('No files available to display.');
        return;
      }
      const { presignedUrl } = await this.fileApi.getPresignedDownloadUrl(id, {
        key: first.name,
        presigned: true,
      });
      const isPdf = first.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        this.getDataPdfUrl = presignedUrl;
      } else {
        this.getDataFileDownloadUrl = presignedUrl;
        this.getDataFileName = first.name;
      }
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to load file from file server.');
    } finally {
      this.getDataLoading = false;
    }
  }

  onGetDataModalToggle(visible: boolean): void {
    this.showGetDataModal = visible;
    if (!visible) {
      if (this.getDataPdfUrl?.startsWith('blob:'))
        URL.revokeObjectURL(this.getDataPdfUrl);
      if (this.getDataFileDownloadUrl?.startsWith('blob:'))
        URL.revokeObjectURL(this.getDataFileDownloadUrl);
      this.getDataPdfUrl = null;
      this.getDataFileDownloadUrl = null;
      this.getDataFileName = '';
    }
  }

  async onDownload(): Promise<void> {
    try {
      const loggedIn = await this.keycloak.isLoggedIn();
      if (!loggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    const id = this.datasetId();
    if (!id) {
      this.toaster.error('Dataset ID not available');
      return;
    }

    try {
      const { downloadUrl } = await this.fileApi.getDatabankDownloadUrl(id);
      if (!downloadUrl) {
        this.toaster.error('No download URL received.');
        return;
      }
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${id}_databank.zip`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toaster.success('Download started successfully');
    } catch (error: unknown) {
      console.error(error);
      const err = error as { status?: number; error?: { code?: string } };
      if (err?.status === 404 || err?.error?.code === 'RESOURCE_NOT_FOUND') {
        await this.fallbackDownloadCsv(id);
        return;
      }
      await this.fallbackDownloadCsv(id);
    }
  }

  private async fallbackDownloadCsv(id: string): Promise<void> {
    try {
      const blob = await this.rsApi.downloadCSVNgslid(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dataset-${id}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.toaster.success('CSV file downloaded successfully');
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to download file');
    }
  }

  async onRequestAccess(): Promise<void> {
    try {
      const loggedIn = await this.keycloak.isLoggedIn();
      if (!loggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }
    this.resetRequestFormPartial();
    this.showRequestAccessModal = true;
    await this.loadUserOrganisation();
  }

  private resetRequestFormPartial(): void {
    this.requestForm = {
      requestAs: 'Organisation',
      organisationName: '',
      purposeDescription: '',
      agreeToTerms: false,
    };
    this.isOrganisationNameDisabled = false;
    this.userHasOrganisation = false;
  }

  closeRequestAccessModal(): void {
    this.showRequestAccessModal = false;
  }

  goToJoinOrganisation(): void {
    this.closeRequestAccessModal();
    void this.router.navigate(['/consumer/join-organisation']);
  }

  async loadUserOrganisation(): Promise<void> {
    try {
      const userData = await this.authApi.getUserInfo();
      if (!userData || typeof userData !== 'object') return;
      const r = userData as Record<string, unknown>;
      const result = (r['result'] ?? r) as Record<string, unknown>;
      let organisationStatus: string | null = null;
      let organisationName: string | null = null;

      const org = result['organisation'] as Record<string, unknown> | undefined;
      if (org) {
        const rawStatus = org['status'];
        organisationStatus = rawStatus
          ? String(rawStatus).toLowerCase().trim()
          : null;
        if (organisationStatus !== 'pending') {
          organisationName =
            (org['organisationName'] as string) ||
            (org['name'] as string) ||
            null;
        }
      }

      if (!organisationName && organisationStatus !== 'pending') {
        const organisationId =
          (org?.['organisationId'] as string) || null;
        if (organisationId) {
          const organisationsResponse =
            await this.authApi.getAllOrganisations(1, 100);
          let organisations: unknown[] = [];
          if (Array.isArray(organisationsResponse)) {
            organisations = organisationsResponse;
          } else if (
            organisationsResponse &&
            typeof organisationsResponse === 'object'
          ) {
            const or = organisationsResponse as Record<string, unknown>;
            const res = or['result'];
            if (Array.isArray(res)) organisations = res;
            else if (res && typeof res === 'object') {
              const inner = (res as Record<string, unknown>)['organisations'];
              if (Array.isArray(inner)) organisations = inner;
            }
          }
          const matched = organisations.find((o: unknown) => {
            const row = o as Record<string, unknown>;
            const oid =
              row['id'] ?? row['organisationId'] ?? row['organisation_id'];
            return oid === organisationId;
          }) as Record<string, unknown> | undefined;
          if (matched) {
            organisationName =
              (matched['orgName'] as string) ||
              (matched['name'] as string) ||
              (matched['organisationName'] as string) ||
              null;
          }
        }
      }

      if (organisationName && organisationStatus !== 'pending') {
        this.requestForm.organisationName = organisationName;
        this.requestForm.requestAs = 'Organisation';
        this.isOrganisationNameDisabled = true;
        this.userHasOrganisation = true;
      } else {
        this.isOrganisationNameDisabled = false;
        this.userHasOrganisation = false;
      }
    } catch (e) {
      console.error(e);
      this.isOrganisationNameDisabled = false;
      this.userHasOrganisation = false;
    }
  }

  async submitRequestAccess(): Promise<void> {
    if (!this.isRequestFormValid) return;
    const id = this.datasetId();
    if (!id) return;
    this.isSubmittingRequest = true;
    try {
      const payload = {
        itemId: id,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: this.requestForm.requestAs,
          purposeOfRequest: this.requestForm.purposeDescription,
          organisationName:
            this.requestForm.requestAs === 'Organisation'
              ? this.requestForm.organisationName
              : '',
        },
      };
      const response = await this.aclApdApi.createAccessRequest(payload);
      if (response) {
        this.toaster.success("Your request was sent successfully.");
      }
      this.closeRequestAccessModal();
      await this.checkUserAccess();
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to send access request. Please try again.');
    } finally {
      this.isSubmittingRequest = false;
    }
  }

  onLoginModalDismiss(): void {
    this.showLoginModal = false;
    void this.keycloak.isLoggedIn().then(() => {
      this.authRefreshRequested.emit();
      void this.checkUserAccess();
    });
  }
}
